import React, { useState, useEffect } from 'react';
import { createCupon } from '../../services/couponService';
import { fetchPrecioPlanesServicios, PrecioPlanServicio } from '../../services/PrecioPlanServicio';

interface Props {
    onClose: () => void;
    onSuccess: () => void;
}

const AddModalCupon: React.FC<Props> = ({ onClose, onSuccess }) => {
    const [planes, setPlanes] = useState<PrecioPlanServicio[]>([]);
    const [formData, setFormData] = useState({
        codigo: '',
        descripcion: '',
        descuento_monto: null as number | null,
        descuento_porcentaje: null as number | null,
        fecha_inicio: '',
        fecha_fin: '',
        uso_unico_por_cliente: true,
        plan_aplicable_id: null as number | null,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setFormData({
            codigo: '',
            descripcion: '',
            descuento_monto: null,
            descuento_porcentaje: null,
            fecha_inicio: '',
            fecha_fin: '',
            uso_unico_por_cliente: true,
            plan_aplicable_id: null,
        });
        setError('');
    }, [onClose]);

    useEffect(() => {
        fetchPrecioPlanesServicios().then(setPlanes).catch(console.error);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const target = e.target as HTMLInputElement;
        const { name, value, type } = target;

        let val: string | number | boolean | null = value;
        if (type === 'checkbox') val = target.checked;
        else if (type === 'number') val = value === '' ? null : Number(value);

        setFormData((prev) => ({ ...prev, [name]: val }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await createCupon({
                ...formData,
                plan_aplicable_id: formData.plan_aplicable_id,
            });
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
            setError('Error al crear el cupón');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-6 rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-xl relative">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Agregar Nuevo Cupón</h3>
                        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        aria-label="Cerrar modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Grid horizontal con 2 columnas */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Código</label>
                            <input
                                type="text"
                                name="codigo"
                                value={formData.codigo}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                            <input
                                type="text"
                                name="descripcion"
                                value={formData.descripcion}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Descuento Monto (Bs.)</label>
                            <input
                                type="number"
                                name="descuento_monto"
                                value={formData.descuento_monto ?? ''}
                                onChange={handleChange}
                                min={0}
                                step="0.01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Descuento Porcentaje (%)</label>
                            <input
                                type="number"
                                name="descuento_porcentaje"
                                value={formData.descuento_porcentaje ?? ''}
                                onChange={handleChange}
                                min={0}
                                max={100}
                                step="0.01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Inicio</label>
                            <input
                                type="datetime-local"
                                name="fecha_inicio"
                                value={formData.fecha_inicio}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin</label>
                            <input
                                type="datetime-local"
                                name="fecha_fin"
                                value={formData.fecha_fin}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Plan Aplicable</label>
                            <select
                                name="plan_aplicable_id"
                                value={formData.plan_aplicable_id ?? ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todos los planes (sin filtro)</option>
                                {planes.map((plan) => (
                                    <option key={plan.id} value={plan.id}>
                                        {plan.plan_nombre} - {plan.servicio_nombre}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Aquí está el uso único con diseño anterior */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg col-span-2">
                            <div>
                                <span className="block text-sm font-medium text-gray-700 mb-1">
                                    Uso único por cliente
                                </span>
                                <p className="text-xs text-gray-500">
                                    {formData.uso_unico_por_cliente ? 'Sí' : 'No'}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    name="uso_unico_por_cliente"
                                    checked={formData.uso_unico_por_cliente}
                                    onChange={handleChange}
                                />
                                <div
                                    className={`w-10 h-6 rounded-full transition-colors ${formData.uso_unico_por_cliente ? 'bg-[#c1348b]' : 'bg-gray-300'
                                        }`}
                                >
                                    <div
                                        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${formData.uso_unico_por_cliente ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 border-t border-gray-200 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center justify-center min-w-[120px]"
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg
                                        className="animate-spin h-4 w-4 mr-2"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Guardando...
                                </span>
                            ) : (
                                'Crear Cupón'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddModalCupon;
